import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../../configuration/setup/hooks';
import fetchHelper from '../fetchHelper';
type UseFetchRequest<T> = {
    url: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    reduxAction?: ActionCreatorWithPayload<T>;
    autoFetch?: boolean;
};

type UseFetchResponse<T> = {
    data: T | null;
    error: string | null;
    isLoading: boolean;
    fetchData: (body?: unknown) => Promise<T | null | undefined>;
};

export const useFetch = <T>({
    url,
    method = 'GET',
    reduxAction,
    autoFetch = false,
}: UseFetchRequest<T>): UseFetchResponse<T> => {
    const [data, setData] = useState<T | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const dispatch = useAppDispatch();

    const fetchData = useCallback(
        async (body?: unknown) => {
            setIsLoading(true);
            try {
                const response = await fetchHelper<T>(url, { method, body });
                setData(response.data);
                setErrorMessage(null);

                if (response.data && reduxAction) {
                    dispatch(reduxAction(response.data));
                }

                return response.data;
            } catch (error) {
                setErrorMessage(String(error));
            } finally {
                setIsLoading(false);
            }
        },
        [url, method, reduxAction, dispatch]
    );

    useEffect(() => {
        if (autoFetch) {
            fetchData();
        }
    }, [autoFetch, fetchData]);

    return {
        data,
        error: errorMessage,
        isLoading,
        fetchData,
    };
};
